import { IconFileText } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Badge, Card, Dialog, DialogContent, DialogHeader, DialogTitle, Group, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { useUltimoNoNulo } from "@/lib/hooks/use-ultimo-no-nulo";
import { formatearCantidad } from "@/shared/format/cantidad";
import { etiquetaEstatusCompra } from "@/shared/format/estatus-compra";
import { formatearMonto } from "@/shared/format/money";
import type { ConsultaOrden } from "../services/orden-compra.api";

function Campo({ label, valor }: { label: string; valor: ReactNode }) {
  return (
    <Group gap={6} wrap="nowrap" align="baseline">
      <Text size="xs" c="dimmed" className="min-w-22.5">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {valor}
      </Text>
    </Group>
  );
}

function TituloSeccion({ children }: { children: ReactNode }) {
  return (
    <Text size="xs" fw={700} tt="uppercase" c="multiva-600" className="tracking-wide">
      {children}
    </Text>
  );
}

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/** Consulta (solo lectura) de la orden de compra · estilo ME23N con entradas y conciliación. */
export function ConsultaOrdenModal({ orden: ordenProp, onClose }: { orden: ConsultaOrden | null; onClose: () => void }) {
  // Conserva el contenido durante la animación de salida (ver useUltimoNoNulo).
  const orden = useUltimoNoNulo(ordenProp);
  return (
    <Dialog open={ordenProp !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            <Group gap={8} wrap="wrap">
              <IconFileText size={18} />
              <Text fw={600} span>
                Consulta de Orden de Compra
              </Text>
              <Badge variant="light" color="slate">
                Solo lectura
              </Badge>
              {orden && (
                <>
                  <Badge variant="light" color="info">
                    Versión {orden.version}
                  </Badge>
                  <Badge variant="light" color={orden.esUltimaVersion ? "exito" : "advertencia"}>
                    {orden.esUltimaVersion ? "Última versión" : "Versión anterior"}
                  </Badge>
                </>
              )}
            </Group>
          </DialogTitle>
        </DialogHeader>

        {orden && (
          <Stack gap="md">
            {/* Cabecera */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Datos de cabecera</TituloSeccion>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Campo
                    label="No. Orden"
                    valor={
                      <Text span ff="mono">
                        {orden.clave}
                      </Text>
                    }
                  />
                  <Campo label="Fecha" valor={fmtFecha(orden.fecha)} />
                  <Campo label="Proveedor" valor={orden.proveedorNombre} />
                  <Campo
                    label="RFC"
                    valor={
                      <Text span ff="mono">
                        {orden.proveedorRfc}
                      </Text>
                    }
                  />
                  <Campo label="Moneda" valor={orden.moneda} />
                  <Campo label="Estatus" valor={etiquetaEstatusCompra(orden.estatus)} />
                </div>
              </Stack>
            </Card>

            {/* Posiciones */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Detalle por posición</TituloSeccion>
                <div className="overflow-x-auto">
                  <Table verticalSpacing="xs" withRowBorders>
                    <Thead>
                      <Tr>
                        <Th>Línea</Th>
                        <Th>Descripción</Th>
                        <Th align="right">Cantidad</Th>
                        <Th>Unidad</Th>
                        <Th align="right">V. unitario</Th>
                        <Th align="right">Importe</Th>
                        <Th>Cuenta</Th>
                        <Th>CeCo</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {orden.conceptos.map((c, i) => (
                        <Tr key={i}>
                          <Td className="font-mono">{c.lineaPedido}</Td>
                          <Td>{c.descripcion}</Td>
                          <Td align="right">{formatearCantidad(c.cantidad)}</Td>
                          <Td>{c.unidad ?? "—"}</Td>
                          <Td align="right">{formatearMonto(c.valorUnitario, orden.moneda)}</Td>
                          <Td align="right">{formatearMonto(c.importe, orden.moneda)}</Td>
                          <Td>{c.cuentaContable ?? "—"}</Td>
                          <Td>{c.centroCostos ?? "—"}</Td>
                        </Tr>
                      ))}
                      {orden.conceptos.length === 0 && (
                        <Tr>
                          <Td colSpan={8}>
                            <Text size="sm" c="dimmed" ta="center" className="py-2">
                              Sin posiciones.
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </Stack>
            </Card>

            {/* Entradas de almacén */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Entradas de almacén (recepciones)</TituloSeccion>
                <div className="overflow-x-auto">
                  <Table verticalSpacing="xs" withRowBorders>
                    <Thead>
                      <Tr>
                        <Th>Folio</Th>
                        <Th>Fecha</Th>
                        <Th>Referencia</Th>
                        <Th>Tipo</Th>
                        <Th align="right">Subtotal</Th>
                        <Th align="right">IVA</Th>
                        <Th align="right">Total</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {orden.entradas.map((e, i) => (
                        <Tr key={i}>
                          <Td className="font-mono">{e.folio ?? "—"}</Td>
                          <Td>{fmtFecha(e.fecha)}</Td>
                          <Td>{e.referencia ?? "—"}</Td>
                          <Td>
                            {e.esAnulacion ? (
                              <Badge variant="light" color="peligro">
                                Anulación
                              </Badge>
                            ) : (
                              <Badge variant="light" color="exito">
                                Recepción
                              </Badge>
                            )}
                          </Td>
                          <Td align="right">{formatearMonto(e.subTotal, orden.moneda)}</Td>
                          <Td align="right">{formatearMonto(e.impuestos, orden.moneda)}</Td>
                          <Td align="right">{formatearMonto(e.total, orden.moneda)}</Td>
                        </Tr>
                      ))}
                      {orden.entradas.length === 0 && (
                        <Tr>
                          <Td colSpan={7}>
                            <Text size="sm" c="dimmed" ta="center" className="py-2">
                              Sin entradas de almacén registradas.
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </Stack>
            </Card>

            {/* Conciliación por línea */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Conciliación por línea (pedido vs recibido)</TituloSeccion>
                <div className="overflow-x-auto">
                  <Table verticalSpacing="xs" withRowBorders>
                    <Thead>
                      <Tr>
                        <Th>Línea</Th>
                        <Th>Descripción</Th>
                        <Th align="right">Cant. pedida</Th>
                        <Th align="right">Importe pedido</Th>
                        <Th align="right">Cant. recibida</Th>
                        <Th align="right">Importe recibido</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {orden.conciliacion.map((l, i) => (
                        <Tr key={i}>
                          <Td className="font-mono">{l.lineaPedido}</Td>
                          <Td>{l.descripcion}</Td>
                          <Td align="right">{formatearCantidad(l.cantidadPedida)}</Td>
                          <Td align="right">{formatearMonto(l.importePedido, orden.moneda)}</Td>
                          <Td align="right">{formatearCantidad(l.cantidadRecibida)}</Td>
                          <Td align="right">{formatearMonto(l.importeRecibido, orden.moneda)}</Td>
                        </Tr>
                      ))}
                      {orden.conciliacion.length === 0 && (
                        <Tr>
                          <Td colSpan={6}>
                            <Text size="sm" c="dimmed" ta="center" className="py-2">
                              Sin líneas.
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </Stack>
            </Card>

            {/* Disponibilidad para facturación */}
            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Disponibilidad para facturación</TituloSeccion>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <TarjetaMonto label="Recibido (neto)" valor={formatearMonto(orden.recibidoNeto, orden.moneda)} moneda={orden.moneda} />
                  <TarjetaMonto label="Facturado (solicitudes)" valor={formatearMonto(orden.facturado, orden.moneda)} moneda={orden.moneda} color="advertencia-600" />
                  <TarjetaMonto
                    label="Disponible a facturar"
                    valor={formatearMonto(orden.disponible, orden.moneda)}
                    moneda={orden.moneda}
                    color={orden.disponible > 0 ? "exito-600" : "peligro-600"}
                  />
                </div>
                <Text size="xs" c="dimmed">
                  Disponible = recibido en almacén (neto de anulaciones) − ya facturado por solicitudes que aún consumen. El CFDI que cargues no puede exceder el disponible y sus conceptos deben
                  corresponder a líneas recibidas (valor unitario, cantidad, unidad e IVA).
                </Text>
              </Stack>
            </Card>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TarjetaMonto({ label, valor, moneda, color }: { label: string; valor: string; moneda: string; color?: string }) {
  return (
    <Card padding="sm">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Group gap={6} align="baseline" wrap="nowrap">
        <Text size="lg" fw={700} c={color} className="text-[26px] leading-none">
          {valor}
        </Text>
        <Text size="sm" fw={700} c={color} className="leading-none">
          {moneda?.toUpperCase()}
        </Text>
      </Group>
    </Card>
  );
}
